// ============================================================================
// ARCHIVO: query-cache.service.ts
// UBICACIÓN: /services/query-cache.service.ts
// ============================================================================
// 
// ████████╗ █████╗ ███╗   ██╗███████╗████████╗ █████╗  ██████╗██╗  ██╗
// ╚══██╔══╝██╔══██╗████╗  ██║██╔════╝╚══██╔══╝██╔══██╗██╔════╝██║ ██╔╝
//    ██║   ███████║██╔██╗ ██║███████╗   ██║   ███████║██║     █████╔╝ 
//    ██║   ██╔══██║██║╚██╗██║╚════██║   ██║   ██╔══██║██║     ██╔═██╗ 
//    ██║   ██║  ██║██║ ╚████║███████║   ██║   ██║  ██║╚██████╗██║  ██╗
//    ╚═╝   ╚═╝  ╚═╝╚═╝  ╚═══╝╚══════╝   ╚═╝   ╚═╝  ╚═╝ ╚═════╝╚═╝  ╚═╝
//                                                                      
//  ██████╗ ██╗   ██╗███████╗██████╗ ██╗   ██╗                         
// ██╔═══██╗██║   ██║██╔════╝██╔══██╗╚██╗ ██╔╝                         
// ██║   ██║██║   ██║█████╗  ██████╔╝ ╚████╔╝                          
// ██║▄▄ ██║██║   ██║██╔══╝  ██╔══██╗  ╚██╔╝                           
// ╚██████╔╝╚██████╔╝███████╗██║  ██║   ██║                            
//  ╚══▀▀═╝  ╚═════╝ ╚══════╝╚═╝  ╚═╝   ╚═╝                            
//
// ============================================================================
// QUERY CACHE SERVICE - Sistema de Caché Inteligente para Angular
// ============================================================================
// 
// DESCRIPCIÓN:
// ------------
// Servicio inspirado en TanStack Query (React Query) que proporciona:
//   - Caché en memoria con persistencia en localStorage
//   - Deduplicación de peticiones simultáneas
//   - Estrategia "Stale-While-Revalidate" 
//   - Retry automático con backoff exponencial
//   - Prefetching para carga anticipada
//   - Garbage collection automático
//
// CONCEPTOS CLAVE:
// ----------------
// 
// 1. STALE TIME (Tiempo de "Frescura"):
//    - Período durante el cual los datos se consideran "frescos"
//    - Mientras los datos son frescos, se retornan del caché sin revalidar
//    - Valor por defecto: 5 minutos
//
// 2. CACHE TIME (Tiempo de Vida):
//    - Período total que los datos permanecen en caché
//    - Después de este tiempo, los datos se eliminan completamente
//    - Valor por defecto: 30 minutos
//
// 3. ESTADOS DE LOS DATOS:
//
//    ┌─────────────────────────────────────────────────────────────────┐
//    │                                                                 │
//    │   FRESH              STALE                    EXPIRED          │
//    │   ┌────┐             ┌─────┐                  ┌───────┐        │
//    │   │ ✓  │─────────────│ ⚠️  │──────────────────│   ✗   │        │
//    │   └────┘             └─────┘                  └───────┘        │
//    │     │                  │                          │            │
//    │     │                  │                          │            │
//    │  t=0               t=staleTime              t=cacheTime        │
//    │  (fetch)           (5 min)                  (30 min)           │
//    │                                                                 │
//    │   Retornar          Retornar cached          Fetch nuevo       │
//    │   inmediato         + revalidar background   obligatorio       │
//    │                                                                 │
//    └─────────────────────────────────────────────────────────────────┘
//
// 4. STALE-WHILE-REVALIDATE:
//    - Cuando los datos están "stale" pero no "expired":
//      a) Retorna inmediatamente los datos del caché (UX rápida)
//      b) En background, hace una petición para actualizar el caché
//      c) La próxima vez que se pidan, ya estarán frescos
//
// 5. DEDUPLICACIÓN DE REQUESTS:
//    - Si dos componentes piden los mismos datos simultáneamente:
//      a) Solo se hace UNA petición HTTP
//      b) Ambos componentes reciben el mismo resultado
//      c) Se evitan race conditions y peticiones duplicadas
//
//    ┌──────────────┐     ┌──────────────┐
//    │ Componente A │     │ Componente B │
//    └──────┬───────┘     └──────┬───────┘
//           │                    │
//           │  getOptions()      │  getOptions()
//           │                    │
//           └────────┬───────────┘
//                    │
//                    ▼
//           ┌───────────────┐
//           │ ¿Request en   │
//           │    vuelo?     │
//           └───────┬───────┘
//                   │
//          ┌───────┴───────┐
//          │ NO            │ SÍ
//          ▼               ▼
//    ┌──────────┐    ┌──────────────┐
//    │  Crear   │    │  Retornar    │
//    │ request$ │    │  request$    │
//    │ + HTTP   │    │  existente   │
//    └────┬─────┘    └──────┬───────┘
//         │                 │
//         └────────┬────────┘
//                  │
//                  ▼
//           ┌────────────┐
//           │ shareReplay│ ← Ambos reciben el mismo resultado
//           └────────────┘
//
// 6. RETRY CON BACKOFF EXPONENCIAL:
//    - Si una petición falla, reintenta automáticamente
//    - Cada reintento espera el doble de tiempo que el anterior
//    - Evita saturar el servidor cuando está sobrecargado
//
//    ┌─────────────────────────────────────────────────────────────────┐
//    │                                                                 │
//    │   Intento 1    Intento 2    Intento 3    Intento 4             │
//    │      │            │            │            │                   │
//    │      ▼            ▼            ▼            ▼                   │
//    │   ┌────┐       ┌────┐       ┌────┐       ┌────┐                │
//    │   │FAIL│──1s──▶│FAIL│──2s──▶│FAIL│──4s──▶│ OK │ ✓              │
//    │   └────┘       └────┘       └────┘       └────┘                │
//    │                                                                 │
//    │   Si todos fallan después de N intentos → Error al usuario     │
//    │                                                                 │
//    └─────────────────────────────────────────────────────────────────┘
//
// 7. PREFETCHING:
//    - Cargar datos ANTES de que el usuario los necesite
//    - Hace que la UI se sienta instantánea
//    - Útil en: formularios multi-paso, hover, selecciones dependientes
//
//    ┌─────────────────────────────────────────────────────────────────┐
//    │                                                                 │
//    │   PASO 1                     PASO 2                            │
//    │   ┌─────────────┐           ┌─────────────┐                    │
//    │   │             │           │             │                    │
//    │   │  [Form...]  │  ──────▶  │  [Dropdown] │ ← ¡Ya tiene datos! │
//    │   │             │           │             │                    │
//    │   └─────────────┘           └─────────────┘                    │
//    │         │                                                       │
//    │         │ (mientras usuario                                     │
//    │         │  llena el form)                                       │
//    │         ▼                                                       │
//    │   ┌───────────────┐                                            │
//    │   │   Prefetch    │                                            │
//    │   │  datos Paso 2 │                                            │
//    │   └───────────────┘                                            │
//    │                                                                 │
//    └─────────────────────────────────────────────────────────────────┘
//
// 8. CAPAS DE ALMACENAMIENTO:
//
//    ┌─────────────────────────────────────────────────────────────────┐
//    │                                                                 │
//    │   ┌────────────────┐                                           │
//    │   │    MEMORIA     │  ◀── Más rápido (~0.1ms)                  │
//    │   │   (Map JS)     │      Volátil (se pierde al recargar)      │
//    │   └───────┬────────┘                                           │
//    │           │                                                     │
//    │           │ Sincroniza                                         │
//    │           ▼                                                     │
//    │   ┌────────────────┐                                           │
//    │   │  LOCALSTORAGE  │  ◀── Persistente (~5ms)                   │
//    │   │  (Navegador)   │      Sobrevive recargas                   │
//    │   └───────┬────────┘                                           │
//    │           │                                                     │
//    │           │ Hidrata al iniciar                                 │
//    │           ▼                                                     │
//    │   ┌────────────────┐                                           │
//    │   │      API       │  ◀── Fuente de verdad (~100-500ms)        │
//    │   │   (Backend)    │      Datos actualizados                   │
//    │   └────────────────┘                                           │
//    │                                                                 │
//    └─────────────────────────────────────────────────────────────────┘
//
// ARQUITECTURA DEL SERVICIO:
// --------------------------
//
//    ┌─────────────────────────────────────────────────────────────────┐
//    │                     QueryCacheService                           │
//    ├─────────────────────────────────────────────────────────────────┤
//    │                                                                 │
//    │  ┌─────────────────────────────────────────────────────────┐   │
//    │  │                    ESTADO INTERNO                        │   │
//    │  ├─────────────────────────────────────────────────────────┤   │
//    │  │  memoryCache: Map<string, CacheEntry>                   │   │
//    │  │  inFlightRequests: Map<string, Observable>              │   │
//    │  │  config: CacheConfig                                     │   │
//    │  └─────────────────────────────────────────────────────────┘   │
//    │                                                                 │
//    │  ┌─────────────────────────────────────────────────────────┐   │
//    │  │                    API PÚBLICA                           │   │
//    │  ├─────────────────────────────────────────────────────────┤   │
//    │  │  query<T>()        → Obtener datos (principal)          │   │
//    │  │  prefetch<T>()     → Pre-cargar datos                   │   │
//    │  │  invalidate()      → Invalidar caché                    │   │
//    │  │  setQueryData<T>() → Setear datos manualmente           │   │
//    │  │  getQueryState()   → Obtener estado actual              │   │
//    │  │  clear()           → Limpiar todo                       │   │
//    │  └─────────────────────────────────────────────────────────┘   │
//    │                                                                 │
//    │  ┌─────────────────────────────────────────────────────────┐   │
//    │  │                  MÉTODOS PRIVADOS                        │   │
//    │  ├─────────────────────────────────────────────────────────┤   │
//    │  │  executeAndCache()      → Ejecutar y cachear            │   │
//    │  │  executeWithRetry()     → Retry con backoff             │   │
//    │  │  revalidateInBackground() → Revalidación async          │   │
//    │  │  persistToStorage()     → Guardar en localStorage       │   │
//    │  │  hydrateFromStorage()   → Cargar desde localStorage     │   │
//    │  │  scheduleGarbageCollection() → Limpieza periódica       │   │
//    │  └─────────────────────────────────────────────────────────┘   │
//    │                                                                 │
//    └─────────────────────────────────────────────────────────────────┘
//
// ============================================================================

import { inject, Injectable, signal } from '@angular/core';
import { SignalService } from '@services/signals.service';
import { Observable, of, Subject, timer, throwError } from 'rxjs';
import {
    shareReplay,
    tap,
    catchError,
    retry,
    finalize
} from 'rxjs/operators';

// ============================================================================
// INTERFACES Y TIPOS
// ============================================================================

/**
 * Estructura de una entrada en el caché.
 * Contiene los datos más metadatos de tiempo para control de frescura.
 * 
 * @template T - Tipo de los datos almacenados
 * 
 * @example
 * const entry: CacheEntry<User[]> = {
 *   data: [{ id: 1, name: 'John' }],
 *   timestamp: 1699900000000,      // Cuándo se obtuvo
 *   staleAt: 1699900300000,        // Cuándo será "viejo" (5 min después)
 *   expiresAt: 1699901800000       // Cuándo expira (30 min después)
 * };
 */
export interface CacheEntry<T = any> {
    /** Los datos almacenados */
    data: T;

    /** Timestamp (ms) de cuándo se obtuvieron los datos */
    timestamp: number;

    /** Timestamp (ms) de cuándo los datos se consideran "stale" (viejos) */
    staleAt: number;

    /** Timestamp (ms) de cuándo los datos expiran y deben eliminarse */
    expiresAt: number;
}

/**
 * Configuración global del sistema de caché.
 * Puede sobreescribirse por query individual.
 * 
 * @example
 * const config: CacheConfig = {
 *   staleTime: 5 * 60 * 1000,    // 5 minutos
 *   cacheTime: 30 * 60 * 1000,   // 30 minutos
 *   prefix: 'myapp_cache_'
 * };
 */
export interface CacheConfig {
    /** 
     * Tiempo (ms) hasta que los datos se consideran "stale".
     * Durante este período, los datos se retornan sin revalidar.
     * @default 300000 (5 minutos)
     */
    staleTime: number;

    /** 
     * Tiempo (ms) hasta que los datos se eliminan del caché.
     * Después de este tiempo, se fuerza una nueva petición.
     * @default 1800000 (30 minutos)
     */
    cacheTime: number;

    /** 
     * Prefijo para las claves en localStorage.
     * Útil para evitar colisiones con otros datos.
     * @default 'qc_'
     */
    prefix: string;
}

/**
 * Opciones para configurar el comportamiento de retry.
 * 
 * @example
 * const retryOptions: RetryConfig = {
 *   maxRetries: 3,
 *   initialDelay: 1000,
 *   maxDelay: 30000,
 *   retryCondition: (error) => error.status >= 500
 * };
 */
export interface RetryConfig {
    /** 
     * Número máximo de reintentos antes de fallar.
     * @default 3
     */
    maxRetries: number;

    /** 
     * Delay inicial (ms) antes del primer reintento.
     * @default 1000 (1 segundo)
     */
    initialDelay: number;

    /** 
     * Delay máximo (ms) entre reintentos.
     * Evita esperas excesivamente largas.
     * @default 30000 (30 segundos)
     */
    maxDelay: number;

    /** 
     * Función que determina si un error debe reintentarse.
     * Por defecto, solo reintenta errores de servidor (5xx) y de red.
     * @default (error) => error.status >= 500 || error.status === 0
     */
    retryCondition?: (error: any) => boolean;
}

/**
 * Opciones que se pueden pasar a métodos query() y prefetch().
 */
export interface QueryOptions {
    /** Tiempo de frescura específico para esta query */
    staleTime?: number;

    /** Tiempo de vida específico para esta query */
    cacheTime?: number;

    /** Configuración de retry específica para esta query */
    retry?: Partial<RetryConfig> | false;
}

/**
 * Estado actual de una query.
 * Útil para mostrar indicadores de carga o frescura en la UI.
 * 
 * @template T - Tipo de los datos
 */
export interface QueryState<T> {
    /** Los datos actuales (null si no hay) */
    data: T | null;

    /** Si los datos están "stale" (viejos pero usables) */
    isStale: boolean;

    /** Si hay una petición en curso */
    isFetching: boolean;

    /** Timestamp de la última actualización */
    dataUpdatedAt: number | null;

    /** Si los datos están completamente expirados */
    isExpired: boolean;
}

// ============================================================================
// SERVICIO PRINCIPAL
// ============================================================================

@Injectable({
    providedIn: 'root'
})
export class QueryCacheService {

    // ==========================================================================
    // CONFIGURACIÓN
    // ==========================================================================

    private readonly signalService = inject(SignalService);
    private readonly appEnvironment = this.signalService.getSignal<string>('APP_ENVIRONMENT');

    private get isCacheEnabled(): boolean {
        const environmentName = (this.appEnvironment() ?? '').trim().toLowerCase();
        return !['local', 'dev'].includes(environmentName);
    }

    /**
     * Configuración por defecto del caché.
     * Puede modificarse vía setConfig() o sobreescribirse por query.
     */
    private readonly defaultConfig: CacheConfig = {
        staleTime: 3 * 60 * 1000,      // 3 minutos de frescura
        cacheTime: 10 * 60 * 1000,     // 10 minutos de vida total
        prefix: 'qc_'                   // Prefijo en localStorage
    };

    /**
     * Configuración de retry por defecto.
     */
    private readonly defaultRetryConfig: RetryConfig = {
        maxRetries: 3,
        initialDelay: 1000,
        maxDelay: 30000,
        retryCondition: (error: any) => {
            // Reintentar en errores de servidor (5xx) o errores de red (0)
            const status = error?.status ?? 0;
            return status >= 500 || status === 0;
        }
    };

    // ==========================================================================
    // ESTADO INTERNO
    // ==========================================================================

    /**
     * Caché en memoria para acceso ultra-rápido.
     * Se sincroniza con localStorage para persistencia.
     */
    private memoryCache = new Map<string, CacheEntry>();

    /**
     * Mapa de requests HTTP activos.
     * Clave: queryKey, Valor: Observable compartido.
     * Permite deduplicar peticiones simultáneas.
     */
    private inFlightRequests = new Map<string, Observable<any>>();

    /**
     * Subject para emitir eventos de invalidación.
     * Útil para debugging o sincronización de UI.
     */
    private invalidations$ = new Subject<{ key: string; reason: string }>();

    /**
     * Subject para emitir eventos de caché (hit, miss, stale, etc.)
     * Útil para métricas y debugging.
     */
    private cacheEvents$ = new Subject<{
        type: 'hit' | 'miss' | 'stale' | 'expired' | 'revalidate' | 'prefetch';
        key: string;
        timestamp: number;
    }>();

    /**
     * Signal reactivo con estadísticas del caché.
     * Actualizado automáticamente.
     */
    private _stats = signal({
        hits: 0,
        misses: 0,
        staleHits: 0,
        size: 0
    });

    /** Estadísticas públicas (solo lectura) */
    public readonly stats = this._stats.asReadonly();

    /**
     * ID del intervalo de garbage collection.
     * Se usa para poder cancelarlo si es necesario.
     */
    private gcIntervalId: any = null;

    // ==========================================================================
    // CONSTRUCTOR
    // ==========================================================================

    constructor() {
        if (!this.isCacheEnabled) return;

        // 1. Hidratar caché desde localStorage al iniciar
        this.hydrateFromStorage();

        // 2. Programar limpieza periódica de entradas expiradas
        this.scheduleGarbageCollection();

        // 3. Actualizar estadísticas de tamaño
        this.updateSizeStats();

        // 4. Escuchar eventos del navegador para sincronizar tabs
        this.setupCrossTabSync();
    }

    // ==========================================================================
    // API PÚBLICA - MÉTODOS PRINCIPALES
    // ==========================================================================

    /**
     * ═══════════════════════════════════════════════════════════════════════════
     * QUERY - Método principal para obtener datos
     * ═══════════════════════════════════════════════════════════════════════════
     * 
     * Obtiene datos del caché o ejecuta el fetcher si es necesario.
     * Implementa la estrategia "Stale-While-Revalidate".
     * 
     * FLUJO DE DECISIÓN:
     * ┌─────────────────────────────────────────────────────────────────────────┐
     * │                                                                         │
     * │   query(key, fetcher)                                                   │
     * │         │                                                               │
     * │         ▼                                                               │
     * │   ┌───────────────┐                                                    │
     * │   │ ¿Está en      │                                                    │
     * │   │   caché?      │                                                    │
     * │   └───────┬───────┘                                                    │
     * │           │                                                             │
     * │     ┌─────┴─────┐                                                      │
     * │     │           │                                                      │
     * │    SÍ          NO                                                      │
     * │     │           │                                                      │
     * │     ▼           │                                                      │
     * │ ┌─────────┐     │                                                      │
     * │ │¿Fresh?  │     │                                                      │
     * │ └────┬────┘     │                                                      │
     * │      │          │                                                      │
     * │  ┌───┴───┐      │                                                      │
     * │  │       │      │                                                      │
     * │ SÍ      NO      │                                                      │
     * │  │       │      │                                                      │
     * │  ▼       ▼      │                                                      │
     * │ Return  ┌────┐  │                                                      │
     * │ cached  │Stale│  │                                                      │
     * │         │ ?   │  │                                                      │
     * │         └──┬──┘  │                                                      │
     * │            │     │                                                      │
     * │      ┌─────┴─────┐                                                     │
     * │      │           │                                                     │
     * │     SÍ          NO (Expired)                                           │
     * │      │           │                                                     │
     * │      ▼           ▼                                                     │
     * │   Return      ┌─────────────────┐                                      │
     * │   cached +    │ ¿Request en     │                                      │
     * │   revalidate  │   vuelo?        │◀─────────────────┘                   │
     * │   background  └────────┬────────┘                                      │
     * │                        │                                                │
     * │                  ┌─────┴─────┐                                         │
     * │                  │           │                                         │
     * │                 SÍ          NO                                         │
     * │                  │           │                                         │
     * │                  ▼           ▼                                         │
     * │              Return      Ejecutar                                      │
     * │              Observable  fetcher +                                     │
     * │              existente   cachear                                       │
     * │                                                                         │
     * └─────────────────────────────────────────────────────────────────────────┘
     * 
     * @template T - Tipo de datos que retorna el fetcher
     * @param queryKey - Identificador único para esta query
     * @param fetcher - Función que retorna un Observable con los datos
     * @param options - Opciones opcionales (staleTime, cacheTime, retry)
     * @returns Observable<T> con los datos
     * 
     * @example
     * // Uso básico
     * this.queryCache.query(
     *   'users-list',
     *   () => this.http.get<User[]>('/api/users')
     * ).subscribe(users => console.log(users));
     * 
     * @example
     * // Con opciones personalizadas
     * this.queryCache.query(
     *   'critical-data',
     *   () => this.http.get<Data>('/api/critical'),
     *   { 
     *     staleTime: 60000,      // 1 minuto de frescura
     *     cacheTime: 300000,     // 5 minutos de vida
     *     retry: { maxRetries: 5 }
     *   }
     * );
     * 
     * @example
     * // Deshabilitar retry
     * this.queryCache.query(
     *   'optional-data',
     *   () => this.http.get('/api/optional'),
     *   { retry: false }
     * );
     */
    query<T>(
        queryKey: string,
        fetcher: () => Observable<T>,
        options?: QueryOptions
    ): Observable<T> {
        if (!this.isCacheEnabled) return fetcher();

        const staleTime = options?.staleTime ?? this.defaultConfig.staleTime;
        const cacheTime = options?.cacheTime ?? this.defaultConfig.cacheTime;
        const now = Date.now();

        // ────────────────────────────────────────────────────────────────────────
        // PASO 1: Buscar en caché
        // ────────────────────────────────────────────────────────────────────────
        const cached = this.getFromCache<T>(queryKey);

        if (cached) {
            const isStale = now >= cached.staleAt;
            const isExpired = now >= cached.expiresAt;

            // ┌────────────────────────────────────────┐
            // │ CASO A: Datos FRESCOS                  │
            // │ Retornar inmediatamente sin revalidar  │
            // └────────────────────────────────────────┘
            if (!isStale && !isExpired) {
                this.emitCacheEvent('hit', queryKey);
                this.incrementStat('hits');
                return of(cached.data);
            }

            // ┌────────────────────────────────────────┐
            // │ CASO B: Datos STALE (viejos pero OK)   │
            // │ Retornar cached + revalidar background │
            // └────────────────────────────────────────┘
            if (isStale && !isExpired) {
                this.emitCacheEvent('stale', queryKey);
                this.incrementStat('staleHits');
                this.revalidateInBackground(queryKey, fetcher, staleTime, cacheTime, options?.retry);
                return of(cached.data);
            }

            // Si llegamos aquí, los datos están EXPIRADOS
            this.emitCacheEvent('expired', queryKey);
        } else {
            this.emitCacheEvent('miss', queryKey);
            this.incrementStat('misses');
        }

        // ────────────────────────────────────────────────────────────────────────
        // PASO 2: Verificar si ya hay request en vuelo (deduplicación)
        // ────────────────────────────────────────────────────────────────────────
        if (this.inFlightRequests.has(queryKey)) {
            return this.inFlightRequests.get(queryKey)! as Observable<T>;
        }

        // ────────────────────────────────────────────────────────────────────────
        // PASO 3: Ejecutar fetcher y cachear resultado
        // ────────────────────────────────────────────────────────────────────────
        return this.executeAndCache<T>(queryKey, fetcher, staleTime, cacheTime, options?.retry);
    }

    /**
     * ═══════════════════════════════════════════════════════════════════════════
     * PREFETCH - Cargar datos anticipadamente
     * ═══════════════════════════════════════════════════════════════════════════
     * 
     * Carga datos en background para que estén listos cuando el usuario los necesite.
     * No bloquea ni retorna Observable - es "fire and forget".
     * 
     * CASOS DE USO:
     * ┌─────────────────────────────────────────────────────────────────────────┐
     * │                                                                         │
     * │  1. FORMULARIOS MULTI-PASO                                             │
     * │     ┌──────────┐    ┌──────────┐    ┌──────────┐                       │
     * │     │  Paso 1  │───▶│  Paso 2  │───▶│  Paso 3  │                       │
     * │     └──────────┘    └──────────┘    └──────────┘                       │
     * │          │               ▲                                              │
     * │          │               │                                              │
     * │          └───prefetch────┘                                             │
     * │              (mientras usuario llena Paso 1)                           │
     * │                                                                         │
     * │  2. HOVER EN BOTONES/LINKS                                             │
     * │     ┌──────────────┐                                                   │
     * │     │   [Ver más]  │ ◀── hover → prefetch                              │
     * │     └──────────────┘                                                   │
     * │                                                                         │
     * │  3. SELECCIONES DEPENDIENTES                                           │
     * │     País: [España ▼]  ← Al seleccionar                                 │
     * │           │              prefetch ciudades                             │
     * │           ▼                                                             │
     * │     Ciudad: [Madrid ▼] ← Ya tiene datos                                │
     * │                                                                         │
     * └─────────────────────────────────────────────────────────────────────────┘
     * 
     * @template T - Tipo de datos que retorna el fetcher
     * @param queryKey - Identificador único para esta query
     * @param fetcher - Función que retorna un Observable con los datos
     * @param options - Opciones opcionales (staleTime, cacheTime)
     * 
     * @example
     * // Prefetch al hacer hover
     * onHoverButton() {
     *   this.queryCache.prefetch(
     *     'user-details',
     *     () => this.http.get('/api/users/123')
     *   );
     * }
     * 
     * @example
     * // Prefetch en formulario multi-paso
     * ngOnInit() {
     *   // Cargar datos del paso actual
     *   this.loadCurrentStep();
     *   
     *   // Pre-cargar datos del siguiente paso
     *   this.queryCache.prefetch(
     *     'step-2-options',
     *     () => this.http.get('/api/step2/options')
     *   );
     * }
     */
    prefetch<T>(
        queryKey: string,
        fetcher: () => Observable<T>,
        options?: QueryOptions
    ): void {
        if (!this.isCacheEnabled) return;

        // Verificar si ya tenemos datos frescos
        const state = this.getQueryState<T>(queryKey);

        if (state.data && !state.isStale) {
            // Ya tenemos datos frescos, no hacer nada
            return;
        }

        // Si ya hay un request en vuelo, no duplicar
        if (this.inFlightRequests.has(queryKey)) {
            return;
        }

        this.emitCacheEvent('prefetch', queryKey);

        // Ejecutar en background (ignorar resultado)
        this.query(queryKey, fetcher, options).subscribe({
            error: (err) => {
                // Silencioso: errores de prefetch no deben afectar la UI
                console.debug(`[QueryCache] Prefetch error for "${queryKey}":`, err);
            }
        });
    }

    /**
     * ═══════════════════════════════════════════════════════════════════════════
     * INVALIDATE - Marcar datos como obsoletos
     * ═══════════════════════════════════════════════════════════════════════════
     * 
     * Elimina datos del caché para forzar una nueva petición.
     * Acepta string exacto, substring match, o RegExp.
     * 
     * @param queryKeyOrPattern - Clave exacta, patrón string, o RegExp
     * 
     * @example
     * // Invalidar una query específica
     * this.queryCache.invalidate('users-list');
     * 
     * @example
     * // Invalidar todas las queries que contengan 'users'
     * this.queryCache.invalidate('users');
     * 
     * @example
     * // Invalidar con RegExp
     * this.queryCache.invalidate(/^user-\d+$/);
     * 
     * @example
     * // Invalidar después de una mutación
     * this.http.post('/api/users', newUser).subscribe(() => {
     *   this.queryCache.invalidate('users');
     * });
     */
    invalidate(queryKeyOrPattern: string | RegExp): void {
        if (!this.isCacheEnabled) return;

        const keysToInvalidate: string[] = [];

        this.memoryCache.forEach((_, key) => {
            const matches = typeof queryKeyOrPattern === 'string'
                ? key.includes(queryKeyOrPattern)
                : queryKeyOrPattern.test(key);

            if (matches) keysToInvalidate.push(key);
        });

        keysToInvalidate.forEach(key => {
            this.memoryCache.delete(key);
            this.removeFromStorage(key);
            this.inFlightRequests.delete(key);
            this.invalidations$.next({ key, reason: 'manual' });
        });

        this.updateSizeStats();

        if (keysToInvalidate.length > 0) {
            console.debug(`[QueryCache] Invalidated ${keysToInvalidate.length} entries:`, keysToInvalidate);
        }
    }

    /**
     * ═══════════════════════════════════════════════════════════════════════════
     * SET QUERY DATA - Actualizar caché manualmente
     * ═══════════════════════════════════════════════════════════════════════════
     * 
     * Permite setear datos directamente en el caché sin hacer petición HTTP.
     * Útil para:
     * - Optimistic updates (actualizar UI antes de que el server responda)
     * - Evitar refetch después de una mutación cuando ya tenemos los datos
     * - Testing
     * 
     * @template T - Tipo de los datos
     * @param queryKey - Identificador de la query
     * @param data - Datos a almacenar
     * @param options - Opciones de tiempo opcionales
     * 
     * @example
     * // Optimistic update
     * onAddTodo(newTodo: Todo) {
     *   // 1. Obtener datos actuales
     *   const currentTodos = this.queryCache.getQueryState<Todo[]>('todos').data || [];
     *   
     *   // 2. Actualizar caché optimistamente
     *   this.queryCache.setQueryData('todos', [...currentTodos, newTodo]);
     *   
     *   // 3. Hacer la petición real
     *   this.http.post('/api/todos', newTodo).subscribe({
     *     error: () => {
     *       // Revertir si falla
     *       this.queryCache.setQueryData('todos', currentTodos);
     *     }
     *   });
     * }
     */
    setQueryData<T>(
        queryKey: string,
        data: T,
        options?: Pick<QueryOptions, 'staleTime' | 'cacheTime'>
    ): void {
        if (!this.isCacheEnabled) return;

        const staleTime = options?.staleTime ?? this.defaultConfig.staleTime;
        const cacheTime = options?.cacheTime ?? this.defaultConfig.cacheTime;
        const now = Date.now();

        const entry: CacheEntry<T> = {
            data,
            timestamp: now,
            staleAt: now + staleTime,
            expiresAt: now + cacheTime
        };

        this.memoryCache.set(queryKey, entry);
        this.persistToStorage(queryKey, entry);
        this.updateSizeStats();
    }

    /**
     * ═══════════════════════════════════════════════════════════════════════════
     * GET QUERY STATE - Obtener estado actual de una query
     * ═══════════════════════════════════════════════════════════════════════════
     * 
     * Retorna información sobre el estado actual de una query en caché.
     * Útil para mostrar indicadores en la UI o tomar decisiones.
     * 
     * @template T - Tipo de los datos
     * @param queryKey - Identificador de la query
     * @returns Estado actual de la query
     * 
     * @example
     * const state = this.queryCache.getQueryState<User[]>('users');
     * 
     * if (state.isFetching) {
     *   console.log('Cargando...');
     * } else if (state.isStale) {
     *   console.log('Datos disponibles pero desactualizados');
     * } else if (state.data) {
     *   console.log('Datos frescos:', state.data);
     * }
     */
    getQueryState<T>(queryKey: string): QueryState<T> {
        if (!this.isCacheEnabled) {
            return {
                data: null,
                isStale: true,
                isFetching: false,
                dataUpdatedAt: null,
                isExpired: true
            };
        }

        const cached = this.getFromCache<T>(queryKey);
        const now = Date.now();

        if (!cached) {
            return {
                data: null,
                isStale: true,
                isFetching: this.inFlightRequests.has(queryKey),
                dataUpdatedAt: null,
                isExpired: true
            };
        }

        return {
            data: cached.data,
            isStale: now >= cached.staleAt,
            isFetching: this.inFlightRequests.has(queryKey),
            dataUpdatedAt: cached.timestamp,
            isExpired: now >= cached.expiresAt
        };
    }

    /**
     * ═══════════════════════════════════════════════════════════════════════════
     * ON INVALIDATE - Observable de eventos de invalidación
     * ═══════════════════════════════════════════════════════════════════════════
     * 
     * Permite suscribirse a eventos de invalidación del caché.
     * Útil para debugging, logging, o sincronizar UI.
     * 
     * @returns Observable que emite cada vez que se invalida una query
     * 
     * @example
     * this.queryCache.onInvalidate().subscribe(event => {
     *   console.log(`Query "${event.key}" invalidated. Reason: ${event.reason}`);
     * });
     */
    onInvalidate(): Observable<{ key: string; reason: string }> {
        return this.invalidations$.asObservable();
    }

    /**
     * ═══════════════════════════════════════════════════════════════════════════
     * ON CACHE EVENT - Observable de todos los eventos del caché
     * ═══════════════════════════════════════════════════════════════════════════
     * 
     * Emite eventos de hit, miss, stale, expired, revalidate, prefetch.
     * Útil para métricas y debugging avanzado.
     * 
     * @returns Observable de eventos de caché
     */
    onCacheEvent(): Observable<{ type: string; key: string; timestamp: number }> {
        return this.cacheEvents$.asObservable();
    }

    /**
     * ═══════════════════════════════════════════════════════════════════════════
     * CLEAR - Limpiar todo el caché
     * ═══════════════════════════════════════════════════════════════════════════
     * 
     * Elimina todos los datos del caché (memoria y localStorage).
     * Útil al hacer logout o resetear el estado de la aplicación.
     * 
     * @example
     * onLogout() {
     *   this.authService.logout();
     *   this.queryCache.clear();
     * }
     */
    clear(): void {
        if (!this.isCacheEnabled) return;

        this.memoryCache.clear();
        this.inFlightRequests.clear();
        this.clearStorage();
        this.resetStats();
        console.debug('[QueryCache] Cache cleared');
    }

    /**
     * Obtiene todas las claves actualmente en caché.
     * Útil para debugging.
     */
    getKeys(): string[] {
        return Array.from(this.memoryCache.keys());
    }

    // ==========================================================================
    // MÉTODOS PRIVADOS - EJECUCIÓN Y CACHÉ
    // ==========================================================================

    /**
     * Ejecuta el fetcher con retry y guarda el resultado en caché.
     */
    private executeAndCache<T>(
        queryKey: string,
        fetcher: () => Observable<T>,
        staleTime: number,
        cacheTime: number,
        retryOptions?: Partial<RetryConfig> | false
    ): Observable<T> {
        const now = Date.now();

        // Crear Observable con o sin retry
        let request$: Observable<T>;

        if (retryOptions === false) {
            // Retry deshabilitado explícitamente
            request$ = fetcher();
        } else {
            // Aplicar retry con backoff
            request$ = this.executeWithRetry(fetcher, retryOptions);
        }

        // Procesar resultado
        const cachedRequest$ = request$.pipe(
            tap(data => {
                const entry: CacheEntry<T> = {
                    data,
                    timestamp: now,
                    staleAt: now + staleTime,
                    expiresAt: now + cacheTime
                };

                this.memoryCache.set(queryKey, entry);
                this.persistToStorage(queryKey, entry);
                this.updateSizeStats();
            }),
            catchError(err => {
                console.error(`[QueryCache] Fetch error for "${queryKey}":`, err);
                throw err;
            }),
            finalize(() => {
                // Limpiar request en vuelo cuando termine (éxito o error)
                this.inFlightRequests.delete(queryKey);
            }),
            // shareReplay permite que múltiples suscriptores reciban el mismo resultado
            shareReplay({ bufferSize: 1, refCount: true })
        );

        // Registrar como request en vuelo
        this.inFlightRequests.set(queryKey, cachedRequest$);

        return cachedRequest$;
    }

    /**
     * ═══════════════════════════════════════════════════════════════════════════
     * EXECUTE WITH RETRY - Retry con Backoff Exponencial
     * ═══════════════════════════════════════════════════════════════════════════
     * 
     * Ejecuta un Observable con reintentos automáticos.
     * Cada reintento espera el doble de tiempo que el anterior.
     * 
     * ALGORITMO DE BACKOFF:
     * ┌─────────────────────────────────────────────────────────────────────────┐
     * │                                                                         │
     * │   delay = min(initialDelay * 2^(retryCount-1), maxDelay)               │
     * │                                                                         │
     * │   Ejemplo con initialDelay=1000ms, maxDelay=30000ms:                   │
     * │                                                                         │
     * │   Intento 1: 1000ms  * 2^0 = 1000ms  (1 segundo)                       │
     * │   Intento 2: 1000ms  * 2^1 = 2000ms  (2 segundos)                      │
     * │   Intento 3: 1000ms  * 2^2 = 4000ms  (4 segundos)                      │
     * │   Intento 4: 1000ms  * 2^3 = 8000ms  (8 segundos)                      │
     * │   Intento 5: 1000ms  * 2^4 = 16000ms (16 segundos)                     │
     * │   Intento 6: 1000ms  * 2^5 = 30000ms (capped at maxDelay)              │
     * │                                                                         │
     * └─────────────────────────────────────────────────────────────────────────┘
     */
    private executeWithRetry<T>(
        fetcher: () => Observable<T>,
        options?: Partial<RetryConfig>
    ): Observable<T> {
        const config: RetryConfig = {
            ...this.defaultRetryConfig,
            ...options
        };

        return fetcher().pipe(
            retry({
                count: config.maxRetries,
                delay: (error, retryCount) => {
                    // Verificar si este error debe reintentarse
                    if (config.retryCondition && !config.retryCondition(error)) {
                        // No reintentar, propagar el error
                        return throwError(() => error);
                    }

                    // Calcular delay con backoff exponencial
                    const delay = Math.min(
                        config.initialDelay * Math.pow(2, retryCount - 1),
                        config.maxDelay
                    );

                    console.debug(
                        `[QueryCache] Retry ${retryCount}/${config.maxRetries} in ${delay}ms`,
                        error
                    );

                    return timer(delay);
                }
            })
        );
    }

    /**
     * Revalida datos en background (para stale-while-revalidate).
     * No retorna nada - es fire and forget.
     */
    private revalidateInBackground<T>(
        queryKey: string,
        fetcher: () => Observable<T>,
        staleTime: number,
        cacheTime: number,
        retryOptions?: Partial<RetryConfig> | false
    ): void {
        // No revalidar si ya hay un request en vuelo
        if (this.inFlightRequests.has(queryKey)) {
            return;
        }

        this.emitCacheEvent('revalidate', queryKey);

        this.executeAndCache(queryKey, fetcher, staleTime, cacheTime, retryOptions)
            .subscribe({
                error: (err) => {
                    // Silencioso: errores de revalidación no deben romper la UI
                    console.debug(`[QueryCache] Background revalidation error for "${queryKey}":`, err);
                }
            });
    }

    // ==========================================================================
    // MÉTODOS PRIVADOS - ACCESO A CACHÉ
    // ==========================================================================

    /**
     * Obtiene una entrada del caché (primero memoria, luego localStorage).
     */
    private getFromCache<T>(queryKey: string): CacheEntry<T> | null {
        // 1. Buscar en memoria (más rápido)
        if (this.memoryCache.has(queryKey)) {
            return this.memoryCache.get(queryKey) as CacheEntry<T>;
        }

        // 2. Buscar en localStorage (persistente)
        const fromStorage = this.getFromStorage<T>(queryKey);

        if (fromStorage) {
            // Hidratar en memoria para acceso futuro más rápido
            this.memoryCache.set(queryKey, fromStorage);
        }

        return fromStorage;
    }

    // ==========================================================================
    // MÉTODOS PRIVADOS - LOCALSTORAGE
    // ==========================================================================

    /**
     * Genera la clave para localStorage con el prefijo configurado.
     */
    private getStorageKey(queryKey: string): string {
        return `${this.defaultConfig.prefix}${queryKey}`;
    }

    /**
     * Guarda una entrada en localStorage.
     */
    private persistToStorage<T>(queryKey: string, entry: CacheEntry<T>): void {
        try {
            const storageKey = this.getStorageKey(queryKey);
            const serialized = JSON.stringify(entry);
            localStorage.setItem(storageKey, serialized);
        } catch (e) {
            // localStorage lleno o no disponible
            console.warn('[QueryCache] Failed to persist to localStorage:', e);

            // Intentar liberar espacio eliminando entradas expiradas
            this.runGarbageCollection();
        }
    }

    /**
     * Obtiene una entrada de localStorage.
     */
    private getFromStorage<T>(queryKey: string): CacheEntry<T> | null {
        try {
            const storageKey = this.getStorageKey(queryKey);
            const raw = localStorage.getItem(storageKey);

            if (!raw) return null;

            const entry = JSON.parse(raw) as CacheEntry<T>;

            // Verificar si ya expiró
            if (Date.now() >= entry.expiresAt) {
                this.removeFromStorage(queryKey);
                return null;
            }

            return entry;
        } catch {
            return null;
        }
    }

    /**
     * Elimina una entrada de localStorage.
     */
    private removeFromStorage(queryKey: string): void {
        try {
            localStorage.removeItem(this.getStorageKey(queryKey));
        } catch {
            // Ignorar errores
        }
    }

    /**
     * Elimina todas las entradas del caché de localStorage.
     */
    private clearStorage(): void {
        try {
            const keysToRemove: string[] = [];

            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key?.startsWith(this.defaultConfig.prefix)) {
                    keysToRemove.push(key);
                }
            }

            keysToRemove.forEach(key => localStorage.removeItem(key));
        } catch {
            // Ignorar errores
        }
    }

    /**
     * Hidrata el caché en memoria desde localStorage al iniciar.
     */
    private hydrateFromStorage(): void {
        try {
            const now = Date.now();
            let hydratedCount = 0;

            for (let i = 0; i < localStorage.length; i++) {
                const storageKey = localStorage.key(i);

                if (!storageKey?.startsWith(this.defaultConfig.prefix)) continue;

                const raw = localStorage.getItem(storageKey);
                if (!raw) continue;

                try {
                    const entry = JSON.parse(raw) as CacheEntry;
                    const queryKey = storageKey.replace(this.defaultConfig.prefix, '');

                    // Solo hidratar si no ha expirado
                    if (now < entry.expiresAt) {
                        this.memoryCache.set(queryKey, entry);
                        hydratedCount++;
                    } else {
                        // Limpiar entradas expiradas
                        localStorage.removeItem(storageKey);
                    }
                } catch {
                    // JSON inválido, eliminar
                    localStorage.removeItem(storageKey);
                }
            }

            if (hydratedCount > 0) {
                console.debug(`[QueryCache] Hydrated ${hydratedCount} entries from localStorage`);
            }
        } catch {
            // localStorage no disponible
        }
    }

    // ==========================================================================
    // MÉTODOS PRIVADOS - GARBAGE COLLECTION
    // ==========================================================================

    /**
     * Programa la limpieza periódica de entradas expiradas.
     */
    private scheduleGarbageCollection(): void {
        // Ejecutar cada 2 minutos
        this.gcIntervalId = setInterval(() => {
            this.runGarbageCollection();
        }, 2 * 60 * 1000);
    }

    /**
     * Ejecuta la limpieza de entradas expiradas.
     */
    private runGarbageCollection(): void {
        const now = Date.now();
        const keysToRemove: string[] = [];

        this.memoryCache.forEach((entry, key) => {
            if (now >= entry.expiresAt) {
                keysToRemove.push(key);
            }
        });

        keysToRemove.forEach(key => {
            this.memoryCache.delete(key);
            this.removeFromStorage(key);
            this.invalidations$.next({ key, reason: 'expired' });
        });

        if (keysToRemove.length > 0) {
            this.updateSizeStats();
            console.debug(`[QueryCache] GC removed ${keysToRemove.length} expired entries`);
        }
    }

    // ==========================================================================
    // MÉTODOS PRIVADOS - SINCRONIZACIÓN ENTRE TABS
    // ==========================================================================

    /**
     * Configura la sincronización entre tabs del navegador.
     * Cuando un tab invalida o actualiza el caché, otros tabs se enteran.
     */
    private setupCrossTabSync(): void {
        if (typeof window === 'undefined') return;

        window.addEventListener('storage', (event) => {
            // Solo nos interesan eventos de nuestro prefijo
            if (!event.key?.startsWith(this.defaultConfig.prefix)) return;

            const queryKey = event.key.replace(this.defaultConfig.prefix, '');

            if (event.newValue === null) {
                // Entrada eliminada en otro tab
                this.memoryCache.delete(queryKey);
            } else if (event.newValue) {
                // Entrada actualizada en otro tab
                try {
                    const entry = JSON.parse(event.newValue) as CacheEntry;
                    this.memoryCache.set(queryKey, entry);
                } catch {
                    // JSON inválido
                }
            }
        });
    }

    // ==========================================================================
    // MÉTODOS PRIVADOS - ESTADÍSTICAS
    // ==========================================================================

    /**
     * Emite un evento de caché para tracking.
     */
    private emitCacheEvent(
        type: 'hit' | 'miss' | 'stale' | 'expired' | 'revalidate' | 'prefetch',
        key: string
    ): void {
        this.cacheEvents$.next({ type, key, timestamp: Date.now() });
    }

    /**
     * Incrementa un contador de estadísticas.
     */
    private incrementStat(stat: 'hits' | 'misses' | 'staleHits'): void {
        this._stats.update(current => ({
            ...current,
            [stat]: current[stat] + 1
        }));
    }

    /**
     * Actualiza la estadística de tamaño del caché.
     */
    private updateSizeStats(): void {
        this._stats.update(current => ({
            ...current,
            size: this.memoryCache.size
        }));
    }

    /**
     * Resetea las estadísticas.
     */
    private resetStats(): void {
        this._stats.set({ hits: 0, misses: 0, staleHits: 0, size: 0 });
    }

    // ==========================================================================
    // CLEANUP
    // ==========================================================================

    /**
     * Limpia recursos al destruir el servicio.
     * Nota: Como es providedIn: 'root', normalmente no se destruye,
     * pero es buena práctica tenerlo.
     */
    ngOnDestroy(): void {
        if (this.gcIntervalId) {
            clearInterval(this.gcIntervalId);
        }
        this.invalidations$.complete();
        this.cacheEvents$.complete();
    }
}
